import React, { useState, useEffect, useCallback } from 'react';
import {
  Building, Plus, Edit2, Trash2, FileText, Database, Search, Filter, Folder, FolderPlus, X, Check, Loader2, FileCode
} from 'lucide-react';
import { Supplier } from '../types/contract.types';
import { useAuth } from '../contexts/AuthContext';
import { contractGroupService, ContractGroup } from '../services/api';
import { TemplateManagement } from '../components/features/master-data/TemplateManagement';

interface ContractTypeCategory {
  id: string;
  name: string;
  types: ContractTypeOption[];
}

interface ContractTypeOption {
  id: string;
  name: string;
  category: string;
}

interface MasterDataProps {
  suppliers: Supplier[];
  contracts: any[];
  contractTypes: ContractTypeCategory[];
  onAddSupplier: () => void;
  onEditSupplier: (supplier: Supplier) => void;
  onDeleteSupplier: (id: number) => void;
  onUpdateContractTypes: (types: ContractTypeCategory[]) => void;
}

// Predefined color options for groups
const colorOptions = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#ef4444', label: 'Red' },
  { value: '#f97316', label: 'Orange' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#6b7280', label: 'Gray' },
];

const MasterData: React.FC<MasterDataProps> = ({
  suppliers,
  contracts,
  contractTypes,
  onAddSupplier,
  onEditSupplier,
  onDeleteSupplier,
  onUpdateContractTypes
}) => {
  const { token } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState('suppliers');
  const [editingType, setEditingType] = useState<{ categoryId: string; typeId?: string } | null>(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Contract Groups state
  const [contractGroups, setContractGroups] = useState<ContractGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ContractGroup | null>(null);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    color: '#6366f1'
  });
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupError, setGroupError] = useState<string | null>(null);

  // Load contract groups
  const loadContractGroups = useCallback(async () => {
    if (!token) return;
    setGroupsLoading(true);
    try {
      const groups = await contractGroupService.getAll(token);
      setContractGroups(groups);
    } catch (error) {
      console.error('Error loading contract groups:', error);
    } finally {
      setGroupsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeSubTab === 'groups') {
      loadContractGroups();
    }
  }, [activeSubTab, loadContractGroups]);

  // Group form handlers
  const handleOpenGroupModal = (group?: ContractGroup) => {
    if (group) {
      setEditingGroup(group);
      setGroupFormData({
        name: group.name,
        description: group.description || '',
        color: group.color || '#6366f1'
      });
    } else {
      setEditingGroup(null);
      setGroupFormData({
        name: '',
        description: '',
        color: '#6366f1'
      });
    }
    setGroupError(null);
    setShowGroupModal(true);
  };

  const handleCloseGroupModal = () => {
    setShowGroupModal(false);
    setEditingGroup(null);
    setGroupFormData({ name: '', description: '', color: '#6366f1' });
    setGroupError(null);
  };

  const handleSaveGroup = async () => {
    if (!token) return;
    if (!groupFormData.name.trim()) {
      setGroupError('Group name is required');
      return;
    }

    setGroupSaving(true);
    setGroupError(null);

    try {
      if (editingGroup) {
        await contractGroupService.update(token, editingGroup.id || editingGroup._id!, {
          name: groupFormData.name.trim(),
          description: groupFormData.description.trim() || undefined,
          color: groupFormData.color
        });
      } else {
        await contractGroupService.create(token, {
          name: groupFormData.name.trim(),
          description: groupFormData.description.trim() || undefined,
          color: groupFormData.color
        });
      }
      await loadContractGroups();
      handleCloseGroupModal();
    } catch (error: any) {
      setGroupError(error.message || 'Failed to save group');
    } finally {
      setGroupSaving(false);
    }
  };

  const handleDeleteGroup = async (group: ContractGroup) => {
    if (!token) return;
    if (!window.confirm(`Are you sure you want to delete the group "${group.name}"?`)) {
      return;
    }

    try {
      await contractGroupService.delete(token, group.id || group._id!);
      await loadContractGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  // Filter suppliers based on search and filters
  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = searchTerm === '' ||
      (supplier.companyName || supplier.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (supplier.contactPersons?.[0]?.name || supplier.contactPerson || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (supplier.contactPersons?.[0]?.email || supplier.email || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || supplier.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || supplier.category === categoryFilter;

    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Get unique categories from suppliers
  const categories = Array.from(new Set(suppliers.map(s => s.category).filter(Boolean)));

  return (
    <div className="space-y-6">
      {/* Sub-navigation for Master Data */}
      <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveSubTab('suppliers')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                activeSubTab === 'suppliers'
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-secondary'
              }`}
            >
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4" />
                Suppliers
              </div>
            </button>
            <button
              onClick={() => setActiveSubTab('groups')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                activeSubTab === 'groups'
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-secondary'
              }`}
            >
              <div className="flex items-center gap-2">
                <Folder className="w-4 h-4" />
                Contract Groups
              </div>
            </button>
            <button
              onClick={() => setActiveSubTab('contract-types')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                activeSubTab === 'contract-types'
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-secondary'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Contract Types
              </div>
            </button>
            <button
              onClick={() => setActiveSubTab('templates')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                activeSubTab === 'templates'
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-secondary'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileCode className="w-4 h-4" />
                Document Templates
              </div>
            </button>
          </div>
          {activeSubTab === 'suppliers' && (
            <button
              onClick={onAddSupplier}
              className="px-4 py-2 bg-accent-600 dark:bg-accent-500 text-white dark:text-gray-900 rounded-lg hover:bg-accent-700 dark:hover:bg-accent-400 flex items-center gap-2 font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Supplier
            </button>
          )}
          {activeSubTab === 'groups' && (
            <button
              onClick={() => handleOpenGroupModal()}
              className="px-4 py-2 bg-accent-600 dark:bg-accent-500 text-white dark:text-gray-900 rounded-lg hover:bg-accent-700 dark:hover:bg-accent-400 flex items-center gap-2 font-medium transition-colors"
            >
              <FolderPlus className="w-4 h-4" />
              Add Group
            </button>
          )}
        </div>
      </div>

      {/* Suppliers Sub-tab Content */}
      {activeSubTab === 'suppliers' && (
        <div className="space-y-6">
          {/* Suppliers Header with Search and Filters */}
          <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Supplier Management</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Manage your suppliers and their information. Suppliers can be linked to contracts.
                </p>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing {filteredSuppliers.length} of {suppliers.length} suppliers
              </div>
            </div>

            {/* Search and Filter Row */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search suppliers by name, contact, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>

                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                >
                  <option value="all">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>

                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setCategoryFilter('all');
                  }}
                  className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Suppliers Table */}
          {filteredSuppliers.length > 0 ? (
            <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Contact Person
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Contracts
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
                    {filteredSuppliers.map((supplier) => {
                      const supplierContracts = contracts.filter(c => c.supplierId === supplier.id);

                      return (
                        <tr key={supplier.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <Building className="w-8 h-8 text-gray-400 dark:text-gray-500 mr-3" />
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {supplier.companyName || supplier.name}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">{supplier.taxId}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {supplier.contactPersons?.[0]?.name || supplier.contactPerson || '-'}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {supplier.contactPersons?.[0]?.role || 'Primary Contact'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {supplier.contactPersons?.[0]?.email || supplier.email || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {supplier.contactPersons?.[0]?.phone || supplier.phone || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                              {supplier.category || 'Uncategorized'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              supplier.status === 'active'
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-500 text-white'
                            }`}>
                              {supplier.status || 'active'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 dark:text-white">{supplierContracts.length}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {supplierContracts.reduce((sum, c) =>
                                sum + (c.negotiationPoints?.filter((p: any) => p.status === 'pending').length || 0), 0
                              )} pending
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => onEditSupplier(supplier)}
                                className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                                title="Edit supplier"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => onDeleteSupplier(typeof supplier.id === 'string' ? parseInt(supplier.id) : supplier.id)}
                                className="text-error-600 dark:text-error-400 hover:text-error-800 dark:hover:text-error-300 transition-colors"
                                title="Delete supplier"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : suppliers.length === 0 ? (
            <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-8 text-left">
              <Database className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Suppliers Yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">Start by adding your first supplier to the system.</p>
              <button
                onClick={onAddSupplier}
                className="px-4 py-2 bg-accent-600 dark:bg-accent-500 text-white dark:text-gray-900 rounded-lg hover:bg-accent-700 dark:hover:bg-accent-400 inline-flex items-center gap-2 font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add First Supplier
              </button>
            </div>
          ) : (
            <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-8 text-left">
              <Filter className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No suppliers match your search</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">Try adjusting your search criteria or filters.</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setCategoryFilter('all');
                }}
                className="px-4 py-2 bg-primary-600 dark:bg-accent-500 text-white dark:text-gray-900 rounded-lg hover:bg-primary-700 dark:hover:bg-accent-400 font-medium transition-colors"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Contract Groups Sub-tab Content */}
      {activeSubTab === 'groups' && (
        <div className="space-y-6">
          {/* Groups Header */}
          <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contract Groups</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Create groups to organize your contracts. For example: "Supplier Contracts", "Service Agreements", etc.
                </p>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {contractGroups.length} group{contractGroups.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Groups List */}
          {groupsLoading ? (
            <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-8 text-center">
              <Loader2 className="w-8 h-8 text-primary-600 dark:text-accent-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading groups...</p>
            </div>
          ) : contractGroups.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contractGroups.map(group => (
                <div
                  key={group.id || group._id}
                  className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: group.color + '20' }}
                      >
                        <Folder className="w-5 h-5" style={{ color: group.color }} />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">{group.name}</h4>
                        {group.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                            {group.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenGroupModal(group)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded transition-colors"
                        title="Edit group"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteGroup(group)}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded transition-colors"
                        title="Delete group"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-dark-border">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        {contracts.filter(c => c.groupId === (group.id || group._id)).length} contract{contracts.filter(c => c.groupId === (group.id || group._id)).length !== 1 ? 's' : ''}
                      </span>
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: group.color }}
                        title={`Color: ${group.color}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-8 text-left">
              <Folder className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Groups Yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Create groups to organize your contracts into categories like "Supplier Contracts", "Service Agreements", etc.
              </p>
              <button
                onClick={() => handleOpenGroupModal()}
                className="px-4 py-2 bg-accent-600 dark:bg-accent-500 text-white dark:text-gray-900 rounded-lg hover:bg-accent-700 dark:hover:bg-accent-400 inline-flex items-center gap-2 font-medium transition-colors"
              >
                <FolderPlus className="w-4 h-4" />
                Create First Group
              </button>
            </div>
          )}
        </div>
      )}

      {/* Contract Types Management */}
      {activeSubTab === 'contract-types' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Contract Types</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage contract categories and types</p>
              </div>
            </div>

            {/* Contract Type Categories */}
            <div className="space-y-4">
              {contractTypes.map(category => (
                <div key={category.id} className="border border-gray-200 dark:border-dark-border rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">{category.name}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {category.types.map(type => (
                      <div key={type.id} className="flex items-center justify-between bg-gray-50 dark:bg-dark-bg-secondary p-3 rounded">
                        {editingType?.categoryId === category.id && editingType?.typeId === type.id ? (
                          <input
                            type="text"
                            value={newTypeName}
                            onChange={(e) => setNewTypeName(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const updatedTypes = contractTypes.map(cat => {
                                  if (cat.id === category.id) {
                                    return {
                                      ...cat,
                                      types: cat.types.map(t =>
                                        t.id === type.id ? { ...t, name: newTypeName } : t
                                      )
                                    };
                                  }
                                  return cat;
                                });
                                onUpdateContractTypes(updatedTypes);
                                setEditingType(null);
                                setNewTypeName('');
                              }
                            }}
                            onBlur={() => {
                              setEditingType(null);
                              setNewTypeName('');
                            }}
                            className="flex-1 px-2 py-1 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400"
                            autoFocus
                          />
                        ) : (
                          <span className="text-sm text-gray-700 dark:text-gray-300">{type.name}</span>
                        )}
                        <div className="flex items-center gap-2 ml-2">
                          <button
                            onClick={() => {
                              setEditingType({ categoryId: category.id, typeId: type.id });
                              setNewTypeName(type.name);
                            }}
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              const updatedTypes = contractTypes.map(cat => {
                                if (cat.id === category.id) {
                                  return {
                                    ...cat,
                                    types: cat.types.filter(t => t.id !== type.id)
                                  };
                                }
                                return cat;
                              });
                              onUpdateContractTypes(updatedTypes);
                            }}
                            className="text-error-400 dark:text-error-500 hover:text-error-600 dark:hover:text-error-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* Add new type button */}
                    <button
                      onClick={() => {
                        const newType = {
                          id: `type-${Date.now()}`,
                          name: 'New Contract Type',
                          category: category.id
                        };
                        const updatedTypes = contractTypes.map(cat => {
                          if (cat.id === category.id) {
                            return {
                              ...cat,
                              types: [...cat.types, newType]
                            };
                          }
                          return cat;
                        });
                        onUpdateContractTypes(updatedTypes);
                        setEditingType({ categoryId: category.id, typeId: newType.id });
                        setNewTypeName('New Contract Type');
                      }}
                      className="flex items-center justify-center gap-2 bg-gray-50 dark:bg-dark-bg-tertiary p-3 rounded border-2 border-dashed border-gray-300 dark:border-dark-border hover:border-gray-400 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-sm">Add Type</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Document Templates Sub-tab Content */}
      {activeSubTab === 'templates' && (
        <TemplateManagement />
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingGroup ? 'Edit Group' : 'Create Group'}
              </h3>
              <button
                onClick={handleCloseGroupModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {groupError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {groupError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={groupFormData.name}
                  onChange={(e) => setGroupFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Supplier Contracts"
                  className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={groupFormData.description}
                  onChange={(e) => setGroupFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description for this group"
                  rows={3}
                  className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-400 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map(color => (
                    <button
                      key={color.value}
                      onClick={() => setGroupFormData(prev => ({ ...prev, color: color.value }))}
                      className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                        groupFormData.color === color.value ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-600' : ''
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-secondary">
              <button
                onClick={handleCloseGroupModal}
                disabled={groupSaving}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGroup}
                disabled={groupSaving || !groupFormData.name.trim()}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {groupSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {editingGroup ? 'Save Changes' : 'Create Group'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterData;
